import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUpload, FiFileText, FiCheckCircle, FiXCircle, FiLoader } from 'react-icons/fi';
import { api } from '../config/api';

interface ProcessingResult {
  document_id: number;
  filename: string;
  processing_success: boolean;
  processing_status: string;
  extracted_summary?: {
    vendor?: string;
    amount?: number;
    date?: string;
    type?: string;
    description?: string;
  };
  error?: string;
  message: string;
}

export default function ManualProcess() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProcessingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'expense');

    try {
      const response = await api.post('/manual-process/upload-and-process', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Manual Document Processing</h1>
        <p className="mt-2 text-gray-600">
          Upload a document and process it immediately with AI extraction
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        {!result && (
          <div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-gray-900">
                  Click to upload or drag and drop
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  PDF, PNG, JPG up to 10MB
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </label>
            </div>

            {file && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <FiFileText className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="mt-6 flex space-x-4">
              <button
                onClick={handleUpload}
                disabled={!file || isProcessing}
                className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                  !file || isProcessing
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center">
                    <FiLoader className="animate-spin h-5 w-5 mr-2" />
                    Processing...
                  </span>
                ) : (
                  'Upload and Process'
                )}
              </button>

              <button
                onClick={() => navigate('/documents')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {result && (
          <div>
            <div className={`p-4 rounded-lg ${
              result.processing_success ? 'bg-green-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center">
                {result.processing_success ? (
                  <FiCheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <FiXCircle className="h-5 w-5 text-red-500 mr-2" />
                )}
                <h3 className="font-medium">
                  {result.processing_success ? 'Processing Complete' : 'Processing Failed'}
                </h3>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Document Details</h4>
              <dl className="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <dt className="text-gray-500">Document ID:</dt>
                  <dd className="font-medium">{result.document_id}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Filename:</dt>
                  <dd className="font-medium">{result.filename}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status:</dt>
                  <dd className="font-medium">{result.processing_status}</dd>
                </div>
              </dl>
            </div>

            {result.extracted_summary && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-900 mb-2">Extracted Information</h4>
                <dl className="grid grid-cols-1 gap-2 text-sm bg-gray-50 p-4 rounded-lg">
                  <div>
                    <dt className="text-gray-500">Vendor:</dt>
                    <dd className="font-medium">
                      {result.extracted_summary.vendor || 'Not found'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Amount:</dt>
                    <dd className="font-medium">
                      {result.extracted_summary.amount 
                        ? `$${result.extracted_summary.amount}`
                        : 'Not found'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Date:</dt>
                    <dd className="font-medium">
                      {result.extracted_summary.date || 'Not found'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Type:</dt>
                    <dd className="font-medium">
                      {result.extracted_summary.type || 'Not found'}
                    </dd>
                  </div>
                  {result.extracted_summary.description && (
                    <div>
                      <dt className="text-gray-500">Description:</dt>
                      <dd className="font-medium">
                        {result.extracted_summary.description}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {result.error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{result.error}</p>
              </div>
            )}

            <div className="mt-6 flex space-x-4">
              <button
                onClick={handleReset}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Process Another Document
              </button>

              <button
                onClick={() => navigate(`/documents/${result.document_id}`)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                View Document
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}